import React from 'react'
import XmarkIcon from 'client/utils/icons/xmark-icon'
import { SearchIcon } from 'lucide-react'
import {
  DcButton,
  DcFlexBox,
  DcInput,
  DcOverlaySlider,
  DcText,
  SectionContent,
} from 'pixel-base'

interface OrderScannerModalProps {
  isOpen: boolean
  onClose: () => void
  scannedSKU: string
  setScannedSKU: (value: string) => void
  handleScan: (scannedData: string) => void
  scannerRef: React.RefObject<HTMLVideoElement>
}

export const OrderScannerModal: React.FC<OrderScannerModalProps> = ({
  isOpen,
  onClose,
  scannedSKU,
  setScannedSKU,
  handleScan,
  scannerRef,
}) => {
  return (
    <DcOverlaySlider
      title={'Scan Barcode/QR Code'}
      width="60%"
      isOpen={isOpen}
      onClose={onClose}
    >
      <SectionContent>
        <DcFlexBox flexDirection="column" gap="15px">
          <DcText textSize="14px" style={{ color: '#6c757d' }}>
            Position the barcode or QR code in front of the camera. The scanner
            will automatically detect and process the code.
          </DcText>

          {/* Video Container with Scanning Overlay */}
          <DcFlexBox
            style={{
              position: 'relative',
              width: '100%',
              height: '400px',
              border: '2px solid #dee2e6',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            <video
              ref={scannerRef}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
              }}
              autoPlay
              playsInline
              muted
            />

            {/* Scanning Overlay */}
            <DcFlexBox
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '200px',
                height: '200px',
                border: '2px solid #007bff',
                borderRadius: '8px',
                pointerEvents: 'none',
              }}
            >
              {/* Corner Indicators */}
              <DcFlexBox
                style={{
                  position: 'absolute',
                  top: '-2px',
                  left: '-2px',
                  width: '20px',
                  height: '20px',
                  borderTop: '3px solid #007bff',
                  borderLeft: '3px solid #007bff',
                }}
              />
              <DcFlexBox
                style={{
                  position: 'absolute',
                  top: '-2px',
                  right: '-2px',
                  width: '20px',
                  height: '20px',
                  borderTop: '3px solid #007bff',
                  borderRight: '3px solid #007bff',
                }}
              />
              <DcFlexBox
                style={{
                  position: 'absolute',
                  bottom: '-2px',
                  left: '-2px',
                  width: '20px',
                  height: '20px',
                  borderBottom: '3px solid #007bff',
                  borderLeft: '3px solid #007bff',
                }}
              />
              <DcFlexBox
                style={{
                  position: 'absolute',
                  bottom: '-2px',
                  right: '-2px',
                  width: '20px',
                  height: '20px',
                  borderBottom: '3px solid #007bff',
                  borderRight: '3px solid #007bff',
                }}
              />
            </DcFlexBox>

            {/* Scanning Line */}
            <div
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '200px',
                height: '2px',
                background: '#007bff',
                opacity: 0.7,
                pointerEvents: 'none',
              }}
            />
          </DcFlexBox>

          {/* Manual Input Section */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <DcText
              textSize="14px"
              style={{ fontWeight: '600', color: '#495057' }}
            >
              Or manually enter SKU:
            </DcText>
            <DcInput
              name="scanned_sku"
              placeholder="Enter product SKU manually"
              value={scannedSKU}
              onChange={(e) => setScannedSKU(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleScan(scannedSKU)
                }
              }}
              onClick={(e) => e.stopPropagation()}
              data-testid="scanned_sku-input"
            />
          </div>

          {/* Action Buttons */}
          <DcFlexBox gap="10px" justifyContent="flex-end">
            <DcButton
              variant="outline-secondary"
              onClick={onClose}
              withIcon={true}
              size="sm"
            >
              <XmarkIcon size={16} />
              Cancel
            </DcButton>
            <DcButton
              size="sm"
              onClick={() => handleScan(scannedSKU)}
              disabled={!scannedSKU}
              withIcon={true}
            >
              <SearchIcon size={16} />
              Search Product
            </DcButton>
          </DcFlexBox>
        </DcFlexBox>
      </SectionContent>
    </DcOverlaySlider>
  )
}

export default OrderScannerModal
